import * as React from "react";
import { useState, useEffect } from "react";
import { LocalizedLink } from "@/components/LocalizedLink";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowUp, Square, Circle, Check, X, Award, Thermometer, Droplets, Wind, Shield, Sparkles, Zap, Users } from "lucide-react";
import { SEO } from "@/components/SEO";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { useOptimizedObserver } from "@/hooks/useOptimizedObserver";
import { Footer } from "@/components/Footer";
import { ContactForm } from "@/components/ui/contact-form";
import { ProminentCTA } from "@/components/ui/prominent-cta";

// Product Images
import k2ProductImage from "@/assets/k2-lifestyle-luxury.jpg";
import s1ProductImage from "@/assets/s1-hero-egg.jpg";

export default function K2vsS1() {
  const [showBackToTop, setShowBackToTop] = useState(false);
  useScrollToTop();
  useOptimizedObserver();

  useEffect(() => {
    let rafId: number | null = null;
    
    const handleScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        setShowBackToTop(window.scrollY > 400);
        rafId = null;
      });
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const comparisonSpecs = [
    { label: "Design Shape", k2: "Revolutionary Square", s1: "Patent Egg-Shaped", highlight: true },
    { label: "Award", k2: "Modern Design Award", s1: "2022 MUSE Platinum", highlight: true },
    { label: "Dimensions (L×W×H)", k2: "660mm × 400mm × 520mm", s1: "580mm × 430mm × 430mm", highlight: false },
    { label: "Space Saving", k2: "40mm shorter than standard", s1: "Compact spherical form", highlight: false },
    { label: "Product Weight", k2: "55kg", s1: "58kg", highlight: false },
    { label: "Water Tank", k2: "Square design, no pressure limit", s1: "Built-in intelligent, no pressure limit", highlight: false },
    { label: "Flush System", k2: "Super spin siphonic", s1: "Super spin siphonic with booster pump", highlight: false },
    { label: "Flush Volume", k2: "4L / 6L dual flush", s1: "4L / 6L dual flush", highlight: false },
    { label: "Seat Design", k2: "Wide square seat ring", s1: "Ergonomic oval seat", highlight: true },
    { label: "Seat Heating", k2: "4 levels adjustable", s1: "4 levels (Normal-40°C)", highlight: false },
    { label: "Water Heating", k2: "Instant tankless heating", s1: "4 levels (Normal-40°C)", highlight: false },
    { label: "Air Dryer", k2: "Warm air drying system", s1: "4 levels (Normal-55°C)", highlight: false },
    { label: "Auto Open/Close", k2: "✓", s1: "✓", highlight: false },
    { label: "Auto Flush", k2: "✓", s1: "✓", highlight: false },
    { label: "Foot Kick Sensor", k2: "—", s1: "✓ Opens seat ring", highlight: true },
    { label: "LED Display", k2: "✓", s1: "✓ Large screen", highlight: false },
    { label: "Control Method", k2: "Remote + Manual", s1: "One-key rotary knob", highlight: true },
    { label: "Wash Modes", k2: "Female, Rear, Massage", s1: "Female, Rear, Massage, Child", highlight: false },
    { label: "Nozzle Type", k2: "Stainless steel", s1: "ABS antibacterial, self-cleaning", highlight: false },
    { label: "Bowl Coating", k2: "Premium ceramic", s1: "Nano antibacterial glazed", highlight: false },
    { label: "Safety Protection", k2: "IPX4 Waterproof", s1: "7-layer protection system", highlight: true },
    { label: "Power Consumption", k2: "1300W (max)", s1: "1400W (max)", highlight: false },
    { label: "Color Options", k2: "White / Silver Grey", s1: "White", highlight: false },
    { label: "Drain Type", k2: "Wall drain adjustable", s1: "S-trap floor drain", highlight: true },
    { label: "Warranty", k2: "3 Years Comprehensive", s1: "3 Years Comprehensive", highlight: false },
    { label: "Price Range", k2: "Premium", s1: "Luxury", highlight: false }
  ];

  const k2Highlights = [
    { icon: <Square className="w-6 h-6" />, text: "Revolutionary square design breaks tradition" },
    { icon: <Sparkles className="w-6 h-6" />, text: "Wider square seat ring reduces fatigue" },
    { icon: <Zap className="w-6 h-6" />, text: "40mm shorter saves bathroom space" },
    { icon: <Thermometer className="w-6 h-6" />, text: "Two color options (White & Silver Grey)" },
    { icon: <Wind className="w-6 h-6" />, text: "Wall drain with adjustable positioning" }
  ];

  const s1Highlights = [
    { icon: <Circle className="w-6 h-6" />, text: "Award-winning iconic egg shape" },
    { icon: <Award className="w-6 h-6" />, text: "2022 MUSE Platinum Award Winner" },
    { icon: <Shield className="w-6 h-6" />, text: "7-layer safety protection system" },
    { icon: <Users className="w-6 h-6" />, text: "Child wash mode for families" },
    { icon: <Droplets className="w-6 h-6" />, text: "Nano antibacterial glazed bowl" }
  ];

  const productStructuredData = {
    "@context": "https://schema.org",
    "@type": "ComparisonPage",
    "name": "K2 Quadrate vs S1 Super Egg Smart Toilet Comparison",
    "description": "Compare the revolutionary K2 Square design smart toilet with the award-winning S1 Egg-shaped smart toilet. Feature-by-feature comparison guide.",
    "mainEntity": [
      {
        "@type": "Product",
        "name": "K2 Quadrate Smart Toilet",
        "description": "Revolutionary square design smart toilet with space-saving innovation"
      },
      {
        "@type": "Product",
        "name": "S1 Super Egg Smart Toilet",
        "description": "Award-winning egg-shaped smart toilet with 7-layer safety protection"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-stone-50 text-foreground">
      <SEO
        title="K2 Quadrate vs S1 Super Egg | Smart Toilet Compared"
        description="Compare K2 Square design vs S1 Egg-shaped smart toilets. Feature-by-feature comparison of dimensions, wash modes, safety features, and design aesthetics."
        keywords="K2 vs S1, square toilet vs egg toilet, smart toilet comparison, K2 Quadrate, S1 Super Egg, toilet comparison 2025"
        url="/compare/k2-vs-s1"
        structuredData={productStructuredData}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Smart Toilets", url: "/smart-toilets" },
          { name: "K2 vs S1 Comparison", url: "/compare/k2-vs-s1" }
        ]}
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-28 pb-16 px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-12 fade-in-observer">
            <Badge variant="outline" className="border-primary/50 text-primary bg-primary/5 mb-4">
              Product Comparison
            </Badge>
            <h1 className="font-playfair text-4xl md:text-6xl font-light text-primary mb-4">
              K2 Quadrate <span className="text-muted-foreground">vs</span> S1 Super Egg
            </h1>
            <p className="font-inter text-xl text-muted-foreground max-w-3xl mx-auto">
              Two revolutionary designs, each with unique advantages. Compare our square and egg-shaped smart toilets to find your perfect match.
            </p>
          </div>

          {/* Product Cards Side by Side */}
          <div className="grid md:grid-cols-2 gap-8 mb-16 fade-in-observer">
            {/* K2 Card */}
            <Card className="overflow-hidden shadow-luxury border-amber-200/50 hover:shadow-gold transition-all duration-500">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img loading="lazy" decoding="async" 
                  src={k2ProductImage}
                  alt="K2 Quadrate smart toilet with revolutionary square design"
                  className="w-full h-full object-cover"
                />
                <Badge className="absolute top-4 left-4 bg-amber-600 text-white">
                  Square Design
                </Badge>
              </div>
              <CardContent className="p-6">
                <h2 className="font-playfair text-2xl font-bold text-primary mb-2">K2 QUADRATE</h2>
                <p className="text-muted-foreground mb-4">Revolutionary square design with space-saving innovation</p>
                <div className="space-y-3">
                  {k2Highlights.map((item, index) => (
                    <div key={index} className="flex items-center gap-3 text-sm">
                      <span className="text-amber-500">{item.icon}</span>
                      <span className="text-muted-foreground">{item.text}</span>
                    </div>
                  ))}
                </div>
                <Button asChild className="w-full mt-6 bg-amber-600 hover:bg-amber-700">
                  <LocalizedLink to="/products/k2">View K2 Details</LocalizedLink>
                </Button>
              </CardContent>
            </Card>

            {/* S1 Card */}
            <Card className="overflow-hidden shadow-luxury border-teal-200/50 hover:shadow-gold transition-all duration-500">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img loading="lazy" decoding="async" 
                  src={s1ProductImage}
                  alt="S1 Super Egg smart toilet with award-winning spherical design"
                  className="w-full h-full object-cover"
                />
                <Badge className="absolute top-4 left-4 bg-teal-600 text-white">
                  Egg Design
                </Badge>
              </div>
              <CardContent className="p-6">
                <h2 className="font-playfair text-2xl font-bold text-primary mb-2">S1 SUPER EGG</h2>
                <p className="text-muted-foreground mb-4">Award-winning egg shape with premium safety features</p>
                <div className="space-y-3">
                  {s1Highlights.map((item, index) => (
                    <div key={index} className="flex items-center gap-3 text-sm">
                      <span className="text-teal-500">{item.icon}</span>
                      <span className="text-muted-foreground">{item.text}</span>
                    </div>
                  ))}
                </div>
                <Button asChild className="w-full mt-6 bg-teal-600 hover:bg-teal-700">
                  <LocalizedLink to="/products/s1">View S1 Details</LocalizedLink>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 px-6 lg:px-12 bg-white/80 backdrop-blur-sm fade-in-observer">
        <div className="container mx-auto max-w-5xl">
          <h2 className="font-playfair text-3xl font-medium text-primary mb-8 text-center">Feature Comparison</h2>
          
          <div className="bg-white rounded-2xl shadow-luxury overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-3 bg-gradient-to-r from-primary to-primary/90 text-primary-foreground">
              <div className="p-4 font-medium text-sm">Feature</div>
              <div className="p-4 font-medium text-sm text-center border-l border-white/20">
                <span className="flex items-center justify-center gap-2">
                  <Square className="w-4 h-4" /> K2 Quadrate
                </span>
              </div>
              <div className="p-4 font-medium text-sm text-center border-l border-white/20">
                <span className="flex items-center justify-center gap-2">
                  <Circle className="w-4 h-4" /> S1 Super Egg
                </span>
              </div>
            </div>

            {/* Table Body */}
            {comparisonSpecs.map((spec, index) => (
              <div 
                key={index} 
                className={`grid grid-cols-3 border-b border-stone-100 ${
                  spec.highlight ? 'bg-gradient-to-r from-amber-50/50 to-teal-50/50' : 
                  index % 2 === 0 ? 'bg-white' : 'bg-stone-50/50'
                }`}
              >
                <div className="p-4 text-sm font-medium text-muted-foreground flex items-center">
                  {spec.highlight && <Sparkles className="w-3 h-3 text-accent mr-2" />}
                  {spec.label}
                </div>
                <div className="p-4 text-sm text-center border-l border-stone-100">
                  {spec.k2 === "✓" ? (
                    <Check className="w-5 h-5 text-green-500 mx-auto" />
                  ) : spec.k2 === "—" ? (
                    <X className="w-5 h-5 text-stone-300 mx-auto" />
                  ) : (
                    <span className="text-primary font-medium">{spec.k2}</span>
                  )}
                </div>
                <div className="p-4 text-sm text-center border-l border-stone-100">
                  {spec.s1 === "✓" ? (
                    <Check className="w-5 h-5 text-green-500 mx-auto" />
                  ) : spec.s1 === "—" ? (
                    <X className="w-5 h-5 text-stone-300 mx-auto" />
                  ) : (
                    <span className="text-primary font-medium">{spec.s1}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Best For Section */}
      <section className="py-16 px-6 lg:px-12 fade-in-observer">
        <div className="container mx-auto max-w-7xl">
          <h2 className="font-playfair text-3xl font-medium text-primary mb-12 text-center">Which One is Right for You?</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* K2 Best For */}
            <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <Square className="w-8 h-8 text-amber-600" />
                <h3 className="font-playfair text-2xl font-medium text-primary">Choose K2 Quadrate If:</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">You want a bold, modern square design that stands out</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">Your bathroom has limited space (40mm shorter)</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">You prefer a wider seat for maximum comfort</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">You need wall drain installation (adjustable)</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">You want color options (White or Silver Grey)</span>
                </li>
              </ul>
            </div>

            {/* S1 Best For */}
            <div className="bg-gradient-to-br from-teal-50 to-cyan-100/50 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <Circle className="w-8 h-8 text-teal-600" />
                <h3 className="font-playfair text-2xl font-medium text-primary">Choose S1 Super Egg If:</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">You want an award-winning iconic design statement</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">Safety is your top priority (7-layer protection)</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">You have a family with children (child wash mode)</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">You prefer simple one-knob control operation</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">You need S-trap floor drain installation</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <ProminentCTA 
        title="Need Help Deciding?"
        description="Our team can help you choose the perfect smart toilet for your bathroom. Contact us for personalized recommendations."
        primaryCTA={{ text: "Request Consultation", href: "/contact" }}
        secondaryCTA={{ text: "View All Products", href: "/smart-toilets" }}
        showContactButtons={true}
      />

      {/* Contact Form */}
      <section className="py-16 px-6 lg:px-12 bg-muted/30 fade-in-observer">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-playfair text-3xl font-medium text-primary mb-4 text-center">Get Expert Advice</h2>
          <p className="font-inter text-muted-foreground text-center mb-8">
            Tell us about your bathroom and needs for personalized recommendations.
          </p>
          <ContactForm />
        </div>
      </section>

      <Footer />

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-50 bg-primary hover:bg-primary/90 text-primary-foreground p-3 rounded-full shadow-lg transition-all duration-300"
          aria-label="Back to top"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}
